import type {
  CampaignEvaluationRequest,
  CampaignEvaluationResponse,
  TemplateContentResponse,
} from './types';
import { campaignLogger } from './logger';

export interface CampaignClientConfig {
  apiBaseUrl: string;
  tenantId: string;
  templatesApiBaseUrl: string;
}

export class CampaignApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'CampaignApiError';
  }
}

export interface CampaignClient {
  evaluateCampaigns(req: CampaignEvaluationRequest, signal?: AbortSignal): Promise<CampaignEvaluationResponse>;
  fetchTemplate(templateId: string): Promise<TemplateContentResponse>;
}

export function createCampaignClient(config: CampaignClientConfig): CampaignClient {
  const base = config.apiBaseUrl.replace(/\/$/, '');
  const templatesBase = config.templatesApiBaseUrl.replace(/\/$/, '');

  return {
    async evaluateCampaigns(req, signal) {
      const response = await fetch(
        `${base}/v1/public/campaigns/evaluate?tenantId=${encodeURIComponent(config.tenantId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(req),
          signal,
        },
      );

      if (!response.ok) {
        const text = await response.text();
        throw new CampaignApiError(response.status, `Campaign API error ${response.status}: ${text}`);
      }

      const payload = (await response.json()) as { data?: CampaignEvaluationResponse };
      return payload.data ?? { matched: false, matches: [] };
    },

    async fetchTemplate(templateId) {
      const response = await fetch(
        `${templatesBase}/v1/public/templates/${encodeURIComponent(templateId)}?tenantId=${encodeURIComponent(config.tenantId)}`,
        { method: 'GET' },
      );

      if (!response.ok) {
        const text = await response.text();
        throw new CampaignApiError(response.status, `Template API error ${response.status}: ${text}`);
      }

      const raw = (await response.json()) as Record<string, unknown>;
      const template = (raw.template ?? raw) as Record<string, unknown>;
      const rawContent = template.componentTree ?? template.pageContent ?? null;
      const rawBlocks = Array.isArray(rawContent)
        ? rawContent
        : rawContent && typeof rawContent === 'object' && Array.isArray((rawContent as Record<string, unknown>).blocks)
          ? ((rawContent as Record<string, unknown>).blocks as Record<string, unknown>[])
          : null;

      if (!rawBlocks) {
        campaignLogger.warn('Campaign template payload has no renderable blocks', {
          templateId,
          reason: rawContent == null ? 'missing_page_content' : 'invalid_page_content_shape',
          hasComponentTree: template.componentTree != null,
          hasPageContent: template.pageContent != null,
        });
      }

      return {
        id: String(template.id ?? templateId),
        pageContent: rawBlocks
          ? rawBlocks.map((block) => ({
              id: String(block.id ?? ''),
              type: String(block.definitionId ?? block.type ?? 'unknown'),
              props: (block.props ?? {}) as Record<string, unknown>,
              ...(block.layout ? { layout: block.layout } : {}),
            }))
          : null,
        displaySettings: (template.displaySettings ?? null) as TemplateContentResponse['displaySettings'],
      };
    },
  };
}
